import { FieldValue, FieldValues, same, SubProfile, SubProfileOption } from '../schema';

// A preset matches when every one of its `values` keys that is *present* in the
// form values is equal — and at least one such key is present. Keys
// absent from the form (e.g. the IMU fields on the limited installer form) are
// ignored for matching but are still applied/sent when the preset is selected.
//
// `same` rather than `===`: every preset prop is a scalar today, where the two agree, but a prop
// that ever holds a list would otherwise match nothing and silently read as `custom`.
const matchesPreset = (option: SubProfileOption, values: FieldValues): boolean => {
  if (!option.props) return false;
  let present = 0;
  for (const [key, expected] of Object.entries(option.props)) {
    if (values[key] === undefined) continue;
    if (!same(values[key], expected)) return false;
    present += 1;
  }
  return present > 0;
};

// Derives the selected sub-profile from the current form values. Returns the
// first matching preset (order matters), else the `custom` option (the one
// without `values`), else the declared `selected`/first value.
export const deriveSubProfile = (sub: SubProfile, values: FieldValues): string | undefined => {
  const match = sub.options.find((o) => matchesPreset(o, values));
  if (match) return match.value;
  const custom = sub.options.find((o) => !o.props);
  return custom?.value ?? sub.selected ?? sub.options[0]?.value ?? '';
};

// The union of every field any preset controls.
const presetFieldKeys = (sub: SubProfile): Set<string> => {
  const keys = new Set<string>();
  for (const { props } of sub.options) {
    if (!props) continue;
    for (const k of Object.keys(props)) {
      keys.add(k);
    }
  }
  return keys;
};

// Applies a sub-profile selection to the form. Selecting a preset writes its
// field values; selecting `custom` (an option with no `values`) restores every
// preset-controlled field to its stored/initial value, so the unsubmitted
// preset changes are reverted.
export const applySubProfile = (
  sub: SubProfile,
  selectedValue: string,
  initialValues: FieldValues,
  onChange: (id: string, value: FieldValue) => void
): void => {
  const option = sub.options.find((o) => o.value === selectedValue);
  if (option?.props) {
    for (const [key, value] of Object.entries(option.props)) {
      onChange(key, value);
    }
    return;
  }
  for (const key of presetFieldKeys(sub)) {
    onChange(key, initialValues[key]);
  }
};
